/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { Carousel } from './carousel';

const DEFAULT_SKELETON_COUNT = 4;

const styles = cssMap({
	skeletonItem: {
		flexShrink: 0,
		// Uses calc() to account for the gap between the images
		// `token('space.100')` = gap used by `Carousel`
		// Must stay in sync with `fourOrMoreImagesButton` in images.tsx
		// @ts-expect-error – calc() with design tokens is not representable in the strict width type
		width: `calc((100% - 3 * ${token('space.100')}) / 4)`,
	},
});

/**
 * __ImagesSkeleton__
 *
 * A skeleton placeholder that mirrors the layout of the `Images` component
 * while image data is loading. Renders shimmering placeholders inside a
 * `Carousel` container.
 *
 * Provide `aria-label` with a translated string such as "Loading related images"
 * to announce the loading state to screen readers.
 */
export const ImagesSkeleton = function ImagesSkeleton({
	heading,
	'aria-label': ariaLabel,
}: {
	'aria-label'?: string;
	heading?: string;
}): JSX.Element {
	return (
		<Box role="status" aria-label={ariaLabel} aria-busy="true">
			<Carousel heading={heading}>
				{Array.from({ length: DEFAULT_SKELETON_COUNT }, (_, index) => (
					<Box key={index} xcss={styles.skeletonItem}>
						<Skeleton
							width="100%"
							height="150px"
							borderRadius={token('radius.large')}
							isShimmering
							testId={`images-skeleton-item-${index}`}
						/>
					</Box>
				))}
			</Carousel>
		</Box>
	);
};
